import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useTheme } from './hooks/useTheme';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import BottomNav from './components/BottomNav';
import MobileMenu from './components/MobileMenu';
import RoleGuard from './components/RoleGuard';
import useIsMobile from './hooks/useIsMobile';

// Auth pages
import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import IntakeForm from './pages/IntakeForm';
import Referral from './pages/Referral';
import ApplyForm from './pages/ApplyForm';
import HRDocSign from './pages/HRDocSign';
import TrainingLink from './pages/TrainingLink';
import AgencyBoard from './pages/AgencyBoard';

// ── CORE ──────────────────────────────────────────────────────────────────────
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';           // now also handles /patients route
import Leads from './pages/Leads';
import LeadProfile from './pages/LeadProfile';
import ClientForm from './pages/ClientForm';
import ClientProfile from './pages/ClientProfile';
import Tasks from './pages/Tasks';
import PriorAuth from './pages/core/PriorAuth';  // moved to core - unified HH + DME
import Reports from './pages/core/Reports';
import ApprovedCounties from './pages/core/ApprovedCounties';
import ServiceCountyMap from './pages/core/ServiceCountyMap';
import AccessControl from './pages/core/AccessControl';
import NotificationPreferences from './pages/core/NotificationPreferences';       // new
import ActivityLogs from './pages/core/ActivityLogs';                            // new
import Complaints from './pages/core/Complaints';                                // HFRD complaint log
import FaxInbox from './pages/core/FaxInbox';
import ESignatures from './pages/core/ESignatures';                          // everything out for signature, agency-wide                                    // Faxage pipeline: review + traffic
import PoliciesProcedures from './pages/core/PoliciesProcedures';
import PolicyDetail from './pages/core/PolicyDetail';
import AgentDashboard from './pages/core/AgentDashboard';
import CaregiverApp from './pages/core/CaregiverApp';

// ── HOME HEALTH ───────────────────────────────────────────────────────────────
import Pipeline from './pages/Pipeline';
import IntakeSubmissions from './pages/IntakeSubmissions';
import Calendar from './pages/care/Calendar';
import CoverageBoard from './components/scheduling/CoverageBoard';
import Clinical from './pages/clinical/Clinical';
import SupervisorVisitForm from './pages/SupervisorVisitForm';
import SupervisorVisitReport from './pages/SupervisorVisitReport';

// ── HME / DME ─────────────────────────────────────────────────────────────────
import Orders from './pages/dme/Orders';
import Suppliers from './pages/dme/Suppliers';
import DmeItems from './pages/dme/DmeItems';
import Delivery from './pages/dme/Delivery';
import CmnTracking from './pages/dme/CmnTracking';
import DmeAuth from './pages/dme/DmeAuth';
import DmePipeline from './pages/dme/DmePipeline';

// ── FINANCE ───────────────────────────────────────────────────────────────────
import Claims from './pages/finance/Claims';
import PayrollRuns from './pages/finance/PayrollRuns';
import PayrollRunDetail from './pages/finance/PayrollRunDetail';
import FinanceOverview from './pages/finance/FinanceOverview';
import ProfitLoss from './pages/finance/ProfitLoss';
import ServiceCatalog from './pages/core/ServiceCatalog';
import AxisCareImport from './pages/core/AxisCareImport';
import TaskCatalog from './pages/core/TaskCatalog';
import Scheduling from './pages/care/Scheduling';
import AgencyPortalView from './pages/care/AgencyPortalView';
import VisitVerification from './pages/care/VisitVerification';

// ── HR & SETTINGS ─────────────────────────────────────────────────────────────
import Personnel from './pages/hr/Personnel';
import PersonnelProfile from './pages/hr/PersonnelProfile';
import OnboardingPipeline from './pages/hr/OnboardingPipeline';
import TrainingCompliance from './pages/hr/TrainingCompliance';
import DisciplinaryLog from './pages/hr/DisciplinaryLog';
import PerformanceMgmt from './pages/hr/PerformanceMgmt';
import Staff from './pages/Staff';
import GeneralInfo from './pages/GeneralInfo';
import ImportData from './pages/ImportData';
import NurseAssessmentForm from './pages/NurseAssessmentForm';
import NurseFormPublic from './pages/NurseFormPublic';
import QASurveyPublic  from './pages/QASurveyPublic';
import QADashboard     from './pages/core/QADashboard';
import DocPacketPreview from './pages/DocPacketPreview';   // temp: doc workflow PDF preview
import GuidedSignPreview from './pages/GuidedSignPreview'; // dev: guided signing prototype
import PacketBuilder from './pages/PacketBuilder';          // a signature request — staff workspace
import SignatureRequestNew from './pages/SignatureRequestNew'; // send for signature — the three-step start
import PacketSign from './pages/PacketSign';                // public external signing page
import BirthdayPopup from './components/BirthdayPopup';
import UpdateBanner from './components/UpdateBanner';  // "new version available" — staff only


function ProtectedLayout({ children, title, quickLinks }) {
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = React.useState(false);
  useTheme();
  return (
    <div className="app-layout">
      {!isMobile && <Sidebar />}
      <div className="main-content">
        <Topbar title={title} quickLinks={quickLinks} />
        <UpdateBanner />
        <BirthdayPopup />
        <div className="page-content" style={{ paddingBottom: isMobile ? 72 : undefined }}>{children}</div>
        {isMobile && <BottomNav onMenuOpen={() => setMenuOpen(true)} />}
        {isMobile && menuOpen && <MobileMenu onClose={() => setMenuOpen(false)} />}
      </div>
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg)' }}>
      <div className="loading-spinner" />
    </div>
  );
  return user ? children : <Navigate to="/login" replace />;
}

function P({ title, children, minRole, module, quickLinks }) {
  const content = (minRole || module) ? <RoleGuard minRole={minRole} module={module}>{children}</RoleGuard> : children;
  return (
    <RequireAuth>
      <ProtectedLayout title={title} quickLinks={quickLinks}>{content}</ProtectedLayout>
    </RequireAuth>
  );
}

// <Navigate> does not fill in route params, so `to="/clients/:id"` sent every
// old /patients link to a client literally called ":id".
function PatientRedirect({ suffix = '' }) {
  const { id } = useParams();
  return <Navigate to={`/clients/${id}${suffix}`} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public. The non-staff ones (all but /login and /set-password) are also
              listed in public/index.html, which keeps them from offering to
              install CAERA — add a new one there too. */}
          <Route path="/login"        element={<Login />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/intake"       element={<IntakeForm />} />
          <Route path="/referral"     element={<Referral />} />
          <Route path="/nurse-form/:token" element={<NurseFormPublic />} />
          <Route path="/qa-survey/:token"  element={<QASurveyPublic />} />
          <Route path="/sign/:token"       element={<PacketSign />} />
          <Route path="/careers/apply"     element={<ApplyForm />} />
          <Route path="/hr-sign/:token"    element={<HRDocSign />} />
          <Route path="/training-link/:token" element={<TrainingLink />} />
          {/* Read-only staffing board for a contracting agency. The token is
              validated server-side by get-agency-board, which decides the
              agency's slice — this route grants no session of its own. */}
          <Route path="/agency/:token"        element={<AgencyBoard />} />
          <Route path="/"             element={<Navigate to="/dashboard" replace />} />

          {/* ── CORE ── */}
          <Route path="/dashboard"  element={<P title="Dashboard" quickLinks>          <Dashboard />        </P>} />
          <Route path="/clients"            element={<P title="Clients">           <Clients />          </P>} />
          <Route path="/clients/new"        element={<P title="Add Client">        <ClientForm />       </P>} />
          <Route path="/clients/:id"        element={<P title="Client Profile">    <ClientProfile />    </P>} />
          <Route path="/clients/:id/edit"   element={<P title="Edit Client">       <ClientForm />       </P>} />
          <Route path="/clients/:id/assessment" element={<P title="Nurse Assessment"><NurseAssessmentForm /></P>} />

          {/* Legacy /patients routes — redirect to /clients */}
          <Route path="/patients"               element={<Navigate to="/clients" replace />} />
          <Route path="/patients/new"           element={<Navigate to="/clients/new" replace />} />
          <Route path="/patients/:id"           element={<PatientRedirect />} />
          <Route path="/patients/:id/edit"      element={<PatientRedirect suffix="/edit" />} />
          <Route path="/patients/:id/assessment" element={<PatientRedirect suffix="/assessment" />} />

          <Route path="/leads"        element={<P title="Leads">               <Leads />            </P>} />
          <Route path="/leads/:id"   element={<P title="Lead Profile">        <LeadProfile />      </P>} />
          <Route path="/tasks"      element={<P title="Tasks">               <Tasks />            </P>} />
          <Route path="/prior-auth" element={<P title="Prior Auth / DMA">    <PriorAuth />        </P>} />
          <Route path="/reports"    element={<P title="Reports & Export">     <Reports />          </P>} />
          <Route path="/permissions" element={<P title="Access Control" minRole="admin"><AccessControl /></P>} />
          <Route path="/activity-logs" element={<P title="Activity Logs" minRole="coordinator"><ActivityLogs /></P>} />
          <Route path="/complaints"    element={<P title="Client Complaints Log" minRole="coordinator" module="quality"><Complaints /></P>} />
          <Route path="/faxes"         element={<P title="Fax Manager" minRole="coordinator"><FaxInbox /></P>} />
          <Route path="/esign"         element={<P title="e-Signatures" minRole="coordinator"><ESignatures /></P>} />
          <Route path="/agents" element={<P title="AI Agents" minRole="admin"><AgentDashboard /></P>} />
          <Route path="/caregiver-app" element={<P title="Caregiver App" minRole="admin"><CaregiverApp /></P>} />
          <Route path="/notification-preferences" element={<P title="Notification Preferences"><NotificationPreferences /></P>} />
          <Route path="/qa"               element={<P title="QA Surveys" module="quality">           <QADashboard />      </P>} />
          <Route path="/policies"         element={<P title="Policies & Procedures" module="quality"><PoliciesProcedures />    </P>} />
          <Route path="/policies/:sectionNumber" element={<P title="Policy Detail" module="quality"><PolicyDetail /></P>} />
          {/* Developer prototypes: available on the dev server only, never in the
              production build (they used to ship there, admin-only). */}
          {process.env.NODE_ENV !== 'production' && (
            <>
              <Route path="/doc-preview"         element={<P title="Packet Preview" minRole="admin"><DocPacketPreview /></P>} />
              <Route path="/guided-sign-preview" element={<P title="Guided Signing" minRole="admin"><GuidedSignPreview /></P>} />
            </>
          )}
          <Route path="/packets/new"      element={<P title="Send for e-signature" minRole="coordinator"><SignatureRequestNew /></P>} />
          <Route path="/packets/:id"      element={<P title="Signature request" minRole="coordinator"><PacketBuilder /></P>} />

          {/* ── HOME HEALTH ── */}
          <Route path="/pipeline"       element={<P title="Pipeline"          module="hh">              <Pipeline />         </P>} />
          <Route path="/hh/intake"      element={<P title="Intake Queue"      module="hh" minRole="coordinator"><IntakeSubmissions /></P>} />
          <Route path="/hh/calendar"    element={<P title="Calendar"          module="hh">              <Calendar />         </P>} />
          <Route path="/hh/schedule"    element={<Navigate to="/hh/calendar" replace />} />
          <Route path="/hh/scheduling" element={<P title="Schedule (CAERA)" module="scheduling" minRole="coordinator"><Scheduling /></P>} />
          <Route path="/hh/verification" element={<P title="Visit Verification" module="scheduling" minRole="coordinator"><VisitVerification /></P>} />
          <Route path="/hh/evv-feed" element={<Navigate to="/hh/verification?tab=feed" replace />} />
          <Route path="/hh/staffing"    element={<P title="Agency Scheduling" module="hh">              <CoverageBoard />    </P>} />
          <Route path="/hh/agency-portal" element={<P title="Agency Portal" module="hh" minRole="coordinator"><AgencyPortalView /></P>} />
          <Route path="/hh/visits"      element={<Navigate to="/hh/calendar?tab=history" replace />} />
          <Route path="/hh/hours"       element={<Navigate to="/hh/calendar?tab=utilisation" replace />} />
          <Route path="/hh/missed"      element={<Navigate to="/hh/calendar?tab=history" replace />} />
          <Route path="/hh/clinical"    element={<P title="Clinical"          module="hh">              <Clinical />         </P>} />
          <Route path="/hh/assessments" element={<Navigate to="/hh/clinical?tab=assessments" replace />} />
          <Route path="/hh/care-plans"  element={<Navigate to="/hh/clinical?tab=care-plans" replace />} />
          <Route path="/hh/supervisor-visits"             element={<Navigate to="/hh/clinical?tab=supervision" replace />} />
          <Route path="/hh/supervisor-visits/new"         element={<P title="Supervisor Visit"  module="hh">        <SupervisorVisitForm />   </P>} />
          <Route path="/hh/supervisor-visits/:id/report"  element={<P title="Visit Report"      module="hh">        <SupervisorVisitReport /> </P>} />
          <Route path="/hh/supervisor-visits/:id"         element={<P title="Supervisor Visit"  module="hh">        <SupervisorVisitForm />   </P>} />

          {/* Legacy clinical routes */}
          <Route path="/clinical/assessments" element={<Navigate to="/hh/clinical?tab=assessments" replace />} />
          <Route path="/clinical/care-plans"  element={<Navigate to="/hh/clinical?tab=care-plans" replace />} />
          <Route path="/clinical/prior-auth"  element={<Navigate to="/prior-auth" replace />} />

          {/* Legacy care routes */}
          <Route path="/care/schedule" element={<Navigate to="/hh/calendar" replace />} />
          <Route path="/care/visits"   element={<Navigate to="/hh/calendar?tab=history" replace />} />
          <Route path="/care/hours"    element={<Navigate to="/hh/calendar?tab=utilisation" replace />} />
          <Route path="/care/missed"   element={<Navigate to="/hh/calendar?tab=history" replace />} />

          {/* Legacy pipeline route */}
          <Route path="/hh/pipeline"       element={<Navigate to="/pipeline" replace />} />
          <Route path="/intake-submissions" element={<Navigate to="/hh/intake" replace />} />

          {/* ── DME ── */}
          <Route path="/dme/pipeline"  element={<P title="DME Pipeline"      module="dme">        <DmePipeline /> </P>} />
          <Route path="/dme/orders"    element={<P title="Equipment Orders"  module="dme">        <Orders />      </P>} />
          <Route path="/dme/suppliers" element={<P title="Suppliers"         module="dme">        <Suppliers />   </P>} />
          <Route path="/dme/items"     element={<P title="DME Items & Charges" module="dme">      <DmeItems />    </P>} />
          <Route path="/dme/inventory" element={<Navigate to="/dme/suppliers" replace />} />
          <Route path="/dme/delivery"  element={<P title="Delivery Tracking" module="dme">        <Delivery />    </P>} />
          <Route path="/dme/cmn"       element={<P title="CMN Tracking"      module="dme">        <CmnTracking /> </P>} />
          <Route path="/dme/auth"      element={<P title="DME Prior Auth"    module="dme">        <DmeAuth />     </P>} />

          {/* ── FINANCE ── */}
          <Route path="/finance"               element={<P title="Finance"            module="finance"><FinanceOverview /></P>} />
          <Route path="/finance/claims"        element={<P title="Claims"             module="finance"><Claims />          </P>} />
          {/* One billing run, beside the runs list (or full width with ?full=1). */}
          <Route path="/finance/claims/runs/:runId" element={<P title="Claims"        module="finance"><Claims />          </P>} />
          {/* Runs and the tracker were two pages until 2026-09-25; both live under Claims now. */}
          <Route path="/finance/billing-run"   element={<Navigate to="/finance/claims" replace />} />
          <Route path="/finance/payroll"       element={<P title="Payroll Mgmt."      module="finance"><PayrollRuns />     </P>} />
          <Route path="/finance/payroll/:id"   element={<P title="Payroll Mgmt."      module="finance"><PayrollRunDetail /></P>} />
          <Route path="/finance/pl"            element={<P title="P&L"                module="finance"><ProfitLoss />      </P>} />
          {/* Retired 2026-09-25: Revenue & Payroll folded into the Overview; Overhead and Projections are tabs of P&L. */}
          <Route path="/finance/revenue"       element={<Navigate to="/finance" replace />} />
          <Route path="/finance/overhead"      element={<Navigate to="/finance/pl?tab=overhead" replace />} />
          <Route path="/finance/projections"   element={<Navigate to="/finance/pl?tab=projections" replace />} />

          {/* Legacy finance routes */}
          <Route path="/finance/reports" element={<Navigate to="/reports" replace />} />

          {/* ── HR & SETTINGS ── */}
          <Route path="/hr/caregivers"    element={<Navigate to="/hr/staff-records" replace />} />
          <Route path="/hr/staff-records" element={<P title="Personnel" module="hr" minRole="coordinator"><Personnel /></P>} />
          <Route path="/hr/personnel/:id" element={<P title="Personnel Profile" module="hr" minRole="coordinator"><PersonnelProfile /></P>} />
          <Route path="/hr/onboarding"    element={<P title="Onboarding Pipeline" module="hr" minRole="coordinator"><OnboardingPipeline /></P>} />
          <Route path="/hr/training-compliance" element={<P title="Training & Compliance" module="hr" minRole="coordinator"><TrainingCompliance /></P>} />
          <Route path="/hr/disciplinary-log"    element={<P title="Incident Log" module="quality" minRole="coordinator"><DisciplinaryLog /></P>} />
          <Route path="/hr/performance"   element={<P title="Performance Mgmt." module="hr" minRole="manager"><PerformanceMgmt /></P>} />
          {/* User Management lives at /staff; this was a second copy of it. */}
          <Route path="/hr/staff"      element={<Navigate to="/staff" replace />} />
          <Route path="/general-info"  element={<P title="General Info">          <GeneralInfo />  </P>} />
          <Route path="/settings/services" element={<P title="Service Catalog" minRole="admin"><ServiceCatalog /></P>} />
          <Route path="/settings/approved-counties" element={<P title="Approved Counties" minRole="admin"><ApprovedCounties /></P>} />
          <Route path="/settings/axiscare-import" element={<P title="AxisCare Import" minRole="admin"><AxisCareImport /></P>} />
          <Route path="/settings/tasks" element={<P title="Task Catalog" minRole="coordinator"><TaskCatalog /></P>} />
          <Route path="/service-county-map" element={<P title="Service County Map"><ServiceCountyMap /></P>} />
          <Route path="/staff"         element={<P title="User Management" minRole="admin"><Staff /></P>} />
          <Route path="/import"        element={<P title="Import Clients (spreadsheet)" minRole="coordinator"><ImportData /></P>} />

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
